import * as React from 'react';
// mui
import {
  IconButton,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  DialogActions,
  useTheme,
  useMediaQuery,
  Tooltip,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
// icons
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import AddBoxIcon from '@mui/icons-material/AddBox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { ClearIcon } from '@mui/x-date-pickers';
// dayjs
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// redux
import { useDispatch, useSelector } from 'react-redux';
import { clearExcuse as clearTaskExcuse, updateTask } from '../../../store/slices/tasksSlice';
import { selectTaskById, selectTasksByParentId } from '../../../store/selectors/taskSelectors';
import { selectRecurringTasksByParentId } from '../../../store/selectors/recuringTaskSelectors';
import { selectEventsByParentId } from '../../../store/selectors/eventSelectors';
// types
import Task from '../../../interfaces/Task.interface';
import Event from '../../../interfaces/Event.interface';
import RecurringTask from '../../../interfaces/RecurringTask.interface';
// axios
import api from '../../../axios';
import MiniTask from '../../../interfaces/MiniTask.interface';

interface TaskItemActionsProps {
  taskId: number;
  parentTaskId: number | null;
  isHovered: boolean;
  onOpenAddTaskDialog: () => void;
  onOpenAddEventDialog: () => void;
  onOpenAddRecurringTaskDialog: () => void;
  onOpenAddMiniTaskDialog: () => void;
}

export default function TaskItemActions(props: TaskItemActionsProps) {
  // hooks
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // props
  const taskId: number = props.taskId;
  const parentTaskId: number | null = props.parentTaskId;
  const isHovered = props.isHovered;
  const addTaskDialogCallback = props.onOpenAddTaskDialog;
  const addEventDialogCallback = props.onOpenAddEventDialog;
  const addRecurringTaskDialogCallback = props.onOpenAddRecurringTaskDialog;
  const addMiniTaskDialogCallback = props.onOpenAddMiniTaskDialog;

  // redux
  const localTask: Task = useSelector(selectTaskById(taskId)) as Task;
  const localSubtasks: Task[] = useSelector(selectTasksByParentId(taskId));
  const localEvents: Event[] = useSelector(selectEventsByParentId(taskId));
  const localRecurringTasks: RecurringTask[] = useSelector(selectRecurringTasksByParentId(taskId));
  const selectParentTask = parentTaskId
    ? selectTaskById(parentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const localParentTask: Task | null = parentTaskRaw ?? null;

  // local constantss
  const uncompleteFilter = (item: Task | Event | RecurringTask): boolean => {
    return !item.completed;
  }
  const canCompleteTask: boolean = localSubtasks.filter(uncompleteFilter).length == 0 
    && localEvents.filter(uncompleteFilter).length == 0
    && localRecurringTasks.filter((task: RecurringTask) => !task.permanentlyCompleted).length == 0
    && localTask.miniTasks.filter((miniTask: MiniTask) => !miniTask.completed).length == 0;

  // local state
  const [ confirmDeleteDialogStatus, setConfirmDeleteDialogStatus ] = React.useState(false);
  const [ confirmDeleteLoading, setConfirmDeleteLoading ] = React.useState(false);

  const [ mobileMenuAnchorEl, setMobileMenuAnchorEl ] = React.useState<null | HTMLElement>(null);

  const handleMobileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
  };
  
  const handleMobileMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(null);
  };

  const handleLocalCompleteToggle = () => {
    const taskToUpdate: Task = { ...localTask, completed: !localTask.completed }; // do we need to include all this data?

    api.put(`/tasks/normalized/update/${taskToUpdate.id}`, taskToUpdate)
      .then((res) => {
        console.log(res);
        const updatedTask: Task = res.data;
        
        dispatch(updateTask(updatedTask));
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const handleLocalConfirmDeleteButtonClick = () => {
    setConfirmDeleteLoading(true);

    api.delete(`/tasks/normalized/delete/${taskId}`)
      .then((res) => {
        console.log(res);

        if (res.data.affected) {
          setConfirmDeleteLoading(false);
          setConfirmDeleteDialogStatus(false);
        } else {
          console.error('something went wrong with deleting a task!');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setConfirmDeleteLoading(false);
      })
  }

  const handleRemoveExcuse = () => {
    api.delete(`/tasks/excuses/remove/task/${taskId}`)
      .then((res) => {
        console.log(res);

        if (res.data.affected) {
          dispatch(clearTaskExcuse(taskId));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  return (
    <React.Fragment>
      {/* conditionally render buttons if user is hovering over task */}
      { (isHovered && !isMobile) &&
        <React.Fragment>
          { (!localTask.completed && localTask.excused) &&
            <Tooltip title="Remove Excuse">
              <IconButton
                sx={{ color: 'lightgrey', p: isMobile ? 0 : '' }}
                onClick={(e) => { e.stopPropagation(); handleRemoveExcuse(); }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          }

          {/* add mini task button */}
          { !localTask.completed &&
            <Tooltip title="Add Mini Task">
              <IconButton
                sx={{ color: 'lightgrey' }}
                onClick={(e) => { e.stopPropagation(); addMiniTaskDialogCallback(); }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          }

          {/* add subtask button */}
          { !localTask.completed &&
            <Tooltip title="Add Subtask">
              <IconButton
                sx={{ color: 'lightgrey' }}
                onClick={(e) => { e.stopPropagation(); addTaskDialogCallback(); }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          }
          
          {/* add event button */}
          { !localTask.completed &&
            <Tooltip title="Add Event">
              <IconButton
                sx={{ color: 'lightgrey' }}
                onClick={(e) => { e.stopPropagation(); addEventDialogCallback(); }}
              >
                <AddBoxIcon />
              </IconButton>
            </Tooltip>
          }

          {/* add recurring task button */}
          { !localTask.completed &&
            <Tooltip title="Add Recurring Task">
              <IconButton
                sx={{ color: 'lightgrey' }}
                onClick={(e) => { e.stopPropagation(); addRecurringTaskDialogCallback(); }}
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          }

          {/* complete / uncomplete button */}
          { !localParentTask?.completed &&
            <Tooltip title={
              localTask.completed ? 'Undo Task Completion'
                : (!canCompleteTask ? 'Cannot Complete Task' : 'Complete Task')
              }>
              <span>
                <IconButton
                  sx={{ color: 'lightgrey', p: isMobile ? 0 : '' }}
                  disabled={!canCompleteTask}
                  onClick={(e) => { e.stopPropagation(); handleLocalCompleteToggle(); }}
                >
                  { localTask.completed ?
                    <ReplayIcon />
                  :
                    <DoneIcon
                      color={!canCompleteTask ? 'disabled' : 'primary'}
                    />
                  }
                </IconButton>
              </span>
            </Tooltip>
          }

          {/* delete button */}
          <IconButton
            sx={{ color: 'lightgrey' }}
            onClick={(e) => { e.stopPropagation(); setConfirmDeleteDialogStatus(true); }}
          >
            <DeleteIcon />
          </IconButton>
        </React.Fragment>
      }

      { isMobile &&
        <React.Fragment>
          <IconButton
            onClick={handleMobileMenuClick}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={handleMobileMenuClose}>
            {!localTask.completed && (
              <>
                { (!localTask.completed && localTask.excused) && (
                  <MenuItem
                    onClick={(e) => {
                      handleMobileMenuClose(e);
                      handleRemoveExcuse();
                    }}
                  >
                    <ListItemIcon>
                      <ClearIcon />
                    </ListItemIcon>
                    <ListItemText primary="Remove Excuse" />
                  </MenuItem>
                )}

                <MenuItem
                  onClick={(e) => {
                    handleMobileMenuClose(e);
                    addMiniTaskDialogCallback();
                  }}
                >
                  <ListItemIcon>
                    <AddCircleOutlineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Add Mini Task" />
                </MenuItem>

                <MenuItem
                  onClick={(e) => {
                    handleMobileMenuClose(e);
                    addTaskDialogCallback();
                  }}
                >
                  <ListItemIcon>
                    <AddIcon />
                  </ListItemIcon>
                  <ListItemText primary="Add Subtask" />
                </MenuItem>

                <MenuItem
                  onClick={(e) => {
                    handleMobileMenuClose(e);
                    addEventDialogCallback();
                  }}
                >
                  <ListItemIcon>
                    <AddBoxIcon />
                  </ListItemIcon>
                  <ListItemText primary="Add Event" />
                </MenuItem>

                <MenuItem
                  onClick={(e) => {
                    handleMobileMenuClose(e);
                    addRecurringTaskDialogCallback();
                  }}
                >
                  <ListItemIcon>
                    <AddCircleIcon />
                  </ListItemIcon>
                  <ListItemText primary="Add Recurring Task" />
                </MenuItem>
              </>
            )}

            {!localParentTask?.completed && (
              <MenuItem
                disabled={!canCompleteTask}
                onClick={(e) => {
                  if (canCompleteTask) {
                    handleMobileMenuClose(e);
                    handleLocalCompleteToggle();
                  }
                }}
              >
                <ListItemIcon>
                  {localTask.completed ? (
                    <ReplayIcon />
                  ) : (
                    <DoneIcon color={!canCompleteTask ? 'disabled' : 'primary'} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    localTask.completed
                      ? 'Undo Task Completion'
                      : 'Complete Task'
                  }
                />
              </MenuItem>
            )}

            <MenuItem
              onClick={(e) => {
                handleMobileMenuClose(e);
                setConfirmDeleteDialogStatus(true);
              }}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText primary="Delete Task" />
            </MenuItem>
          </Menu>
        </React.Fragment>
      }

      {/* dialog to confirm task deletion */}
      <Dialog
        open={confirmDeleteDialogStatus}
        onClose={(e: any) => { e.stopPropagation(); setConfirmDeleteDialogStatus(false); }}
      >
        <DialogTitle>
          Delete "{localTask.title}"?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deleting this task will delete {localSubtasks.length+localEvents.length+localRecurringTasks.length} subtasks(s).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => { e.stopPropagation(); setConfirmDeleteDialogStatus(false); }}>Cancel</Button>
          <Button loading={confirmDeleteLoading} onClick={(e) => { e.stopPropagation(); handleLocalConfirmDeleteButtonClick(); }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}